import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

import DefaultLogoPath from '../../image/DefaultLogo.png'
import { useLanguage } from '../../Context/LanguageContext';

const MAX_MB = 3;
const ALLOWED = ['jpg', 'jpeg', 'png'];

const LogoFileInput = forwardRef((props, ref) => {
  const { onImageChange, isDragging, onDragStateChange } = props;
  const { t } = useLanguage();
  const FileInputRef = useRef();
	const [imgBase64, setImgBase64] = useState();
	const [imgBase64Thumbnail, setImgBase64Thumbnail] = useState();
  const [imgFile, setImgFile] = useState();
  const [imgLocalPath, setImgLocalPath] = useState('');

	const hiddenFileInput = useRef(null);
  const LogoCanvasRef = useRef();
  const LogoThumbnailCanvasRef = useRef();

  useEffect(() => {
    if (onImageChange) {
      onImageChange(imgBase64, imgFile);
    }
  }, [imgBase64, imgFile, onImageChange]);

  useImperativeHandle(ref, () => ({
    getImgBase64: () => {
      return imgBase64;
    },
    getImgBase64Thumbnail: () => {
      return imgBase64Thumbnail;
    },
    getValue: () => {
      return imgLocalPath;
    },
    setBold: () => {

    },
    reset: () => {
      setImgBase64();
      setImgFile();
      setImgLocalPath();
    },
    triggerFileSelect: () => {
      hiddenFileInput.current?.click();
      FileInputRef.current.style.borderColor = '#FF8B48';
    },
    processFile: (file, fileName) => {
      processImageFile(file, fileName);
    }
  }));

  const handleClick = e => {
    hiddenFileInput.current.click();
    FileInputRef.current.style.borderColor = '#FF8B48';
	};

  // 파일 처리 공통 함수
  const processImageFile = (file, fileName) => {
    const pathpoint = fileName.lastIndexOf('.');
    const filepoint = fileName.substring(pathpoint + 1, fileName.length);
    const filetype = filepoint.toLowerCase();

    if (filetype === 'jpg' || filetype === 'png' || filetype === 'jpeg') {
      // 정상적인 이미지 확장자 파일인 경우
      const reader = new FileReader();

      setImgLocalPath(fileName);

      reader.onloadend = () => {
        const base64 = reader.result;

        const naturalImage = new Image();

        naturalImage.src = reader.result.toString();

        naturalImage.onload = function (e) {
          let naturalWidth = e.target.naturalWidth;
          let naturalHeight = e.target.naturalHeight;

          let resizeWidth = 0;
          let resizeHeight = 0;

          // 원본 처리
          if (naturalWidth < naturalHeight) {
            resizeWidth = 600;
            resizeHeight = (600 * naturalHeight) / naturalWidth;
          }
          else if (naturalWidth > naturalHeight) {
            resizeHeight = 600;
            resizeWidth = (600 * naturalWidth) / naturalHeight;
          }
          else {
            resizeWidth = 600;
            resizeHeight = 600;
          }

          const image = new Image();
          const canvas = LogoCanvasRef.current;
          const ctx = canvas?.getContext('2d');

          if (ctx) {
            canvas.width = 600;
            canvas.height = 600;

            image.src = base64.toString();

            // 사진 크기 조정후 Canvas에 그리기
            image.onload = function () {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(image, ((600 - resizeWidth) / 2), ((600 - resizeHeight) / 2), resizeWidth, resizeHeight);
            }

            // 크기 조절된 사진 데이터 정리하기
            canvas.toBlob(function (blob) {
              setImgBase64(canvas.toDataURL());
            })

            // 축소판 처리
            let resizeThumbnailWidth = 0;
            let resizeThumbnailHeight = 0;

            if (naturalWidth < naturalHeight) {
              resizeThumbnailWidth = 240;
              resizeThumbnailHeight = (240 * naturalHeight) / naturalWidth;
            }
            else if (naturalWidth > naturalHeight) {
              resizeThumbnailHeight = 240;
              resizeThumbnailWidth = (240 * naturalWidth) / naturalHeight;
            }
            else {
              resizeThumbnailWidth = 240;
              resizeThumbnailHeight = 240;
            }

            const thumbnailImage = new Image();
            const thumbnailCanvas = LogoThumbnailCanvasRef.current;
            const thumbnailCtx = thumbnailCanvas?.getContext('2d');

            if (thumbnailCtx) {
              thumbnailCanvas.width = 240;
              thumbnailCanvas.height = 240;

              thumbnailImage.src = base64.toString();

              // 사진 크기 조정후 Canvas에 그리기
              thumbnailImage.onload = function () {
                thumbnailCtx.clearRect(0, 0, thumbnailCanvas.width, thumbnailCanvas.height);
                thumbnailCtx.drawImage(thumbnailImage, ((240 - resizeThumbnailWidth) / 2), ((240 - resizeThumbnailHeight) / 2), resizeThumbnailWidth, resizeThumbnailHeight);
              }

              // 크기 조절된 사진 데이터 정리하기
              thumbnailCanvas.toBlob(function (blob) {
                setImgBase64Thumbnail(thumbnailCanvas.toDataURL());
              })
            }
          }
        }
      };

      if (file) {
        reader.readAsDataURL(file);
        setImgFile(file);
      }
    }
    else {
      alert(t('signUp.logoFileOnly'));
    }
  };

  const handleChange = e => {
    const tmp = e.target.value.split('\\');
    const fileName = tmp[tmp.length - 1];
    const file = e.target.files[0];

    if (file) {
      processImageFile(file, fileName);
    }
	};

  // 드래그 앤 드롭 이벤트 핸들러
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragStateChange?.(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // 드래그가 자식 요소로 이동하는 경우 무시
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      onDragStateChange?.(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragStateChange?.(false);

    const files = e.dataTransfer.files;

    if (files && files.length > 0) {
      const file = files[0];
      const fileName = file.name;
      processImageFile(file, fileName);
    }
  };
  
  return (
    <TopLevelWrapper>
      <LogoExplanationWrapper>
        <LogoExplanation>{t('signUp.logoRecommendation')}</LogoExplanation>
      </LogoExplanationWrapper>
			<ContentWrapper
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <canvas ref={LogoCanvasRef} style={{ display: 'none' }} />
        <canvas ref={LogoThumbnailCanvasRef} style={{ display: 'none' }} />
        <input
          type="file"
          ref={hiddenFileInput}
          onChange={handleChange}
          style={{display: 'none'}}/>
        <InputWrapper ref={FileInputRef} isDragging={isDragging}>
          <FileInput isDragging={isDragging} hasFile={!!imgLocalPath} dropText={t('signUp.logoDropFile')} dragText={t('signUp.logoDragOrClick')}>
            {imgLocalPath}
          </FileInput>
          {/* <InputButton onClick={handleClick}>찾아보기</InputButton> */}
        </InputWrapper>

      </ContentWrapper>
    </TopLevelWrapper>
  );
})

export default LogoFileInput;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 356px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: flex-end;

  cursor: pointer;
`;

const LogoExplanationWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex-wrap: wrap;

  // width: max(140px, 24.375vw);
`;

const LogoExplanation = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;
	// font-size: max(12px, 0.625vw);
	// line-height: 17.38px;

	margin: 8px 0 0 0;

	color: rgba(203, 203, 203, 1);
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-end;

  width: 100%;

  border-bottom: 1px solid #E8E8E8;

  transition: all 0.3s ease-in-out;
`;

const FileInput = styled.div`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  padding: 0 2px 4px 0;
  margin-top: 16px;

  flex: 1;
  height: 25px;

  border: 0px;
  color: ${props => props.isDragging ? '#FF8B48' : props.hasFile ? '#000000' : '#CBCBCB'};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: all 0.3s ease-in-out;

  &:empty::before {
    content: '${props => props.isDragging ? props.dropText : props.dragText}';
    color: ${props => props.isDragging ? '#FF8B48' : '#CBCBCB'};
  }
`;

const InputButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 79px;
  height: 32px;

  border-radius: 8px 8px 0 0;
  background-color: #FF8B48;

  font-family: NotoSansKR-Bold;
  font-size: max(14px, 0.625vw);

  color: #FFFFFF;

  margin-bottom: -1px;

  &:hover {
    cursor: pointer;
  }
`;
